import React from 'react';
import { DEFAULT_ONBOARDING_COPY, type OnboardingCopy } from '../../../types/onboarding';

/**
 * Vocabulary and normalisers shared by the Admin → Onboarding tabs.
 *
 * Everything goes through the panel dispatcher, section "onboarding"
 * (Backend/onboarding_admin.py for config / keywords / go live / funnel /
 * people, Backend/onboarding_picks.py for picks_*). The readers below accept
 * the obvious spellings of each field, so a payload that names a key a little
 * differently (or an older server that does not send it) degrades to "—"
 * rather than an empty tab.
 */

export const S = 'onboarding';

export type Note = { kind: 'error' | 'ok'; text: string } | null;

export type Flow = 'v1' | 'v2';
export type CardDepth = 'as_today' | 'full_except_contact';

export interface OnbConfig {
  flow: Flow;
  explorer_picks: boolean;
  entry_modal: boolean;
  picks_card_depth: CardDepth;
  hidden_industries: string[];
  industry_order: string[];
  copy: Partial<OnboardingCopy>;
  picks: Record<string, number>;
  /** "Lock search for Explorers". Null: this server does not store it yet. */
  lock_search: boolean | null;
  _before_go_live: Record<string, any> | null;
}

export interface IndustryRow {
  name: string;
  chips: string[];
  /** 'admin' | 'corpus' | 'none' */
  source: string;
  /** Who saved an admin edit (from source 'admin:<email>'). */
  by: string;
  updated_at: string | null;
  hidden: boolean;
  /** Strict-match candidates per chip (from the stored picks lists). */
  counts: Record<string, number>;
  /** Industry-only list candidates. */
  candidates: number | null;
}

export interface Checklist {
  lock: { enabled: boolean | null; can_search: boolean | null; preview_results: number | null; can_filter: boolean | null };
  picks_block_enabled: boolean | null;
  signin_modal_enabled: boolean | null;
  picks_built_at: string | null;
  thin_count: number | null;
  ids_missing: number | null;
}

export interface ConfigData {
  config: OnbConfig;
  defaults: Required<OnboardingCopy>;
  config_version: string;
  industries: IndustryRow[];
  /** Every name the dropdown could show (profile spec), hidden ones included. */
  all_names: string[];
  checklist: Checklist;
  raw: any;
}

const arr = (v: any): any[] => (Array.isArray(v) ? v : []);
const str = (v: any): string => (v === null || v === undefined ? '' : String(v));
const boolOrNull = (v: any): boolean | null => (typeof v === 'boolean' ? v : null);
const numOrNull = (v: any): number | null => {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN);
  return Number.isFinite(n) ? n : null;
};
export const first = (...vals: any[]) => vals.find(v => v !== undefined && v !== null);

export const DEFAULT_CONFIG: OnbConfig = {
  flow: 'v1', explorer_picks: true, entry_modal: false, picks_card_depth: 'full_except_contact',
  hidden_industries: [], industry_order: [], copy: {}, picks: {}, lock_search: null, _before_go_live: null,
};

export function normConfig(c: any): OnbConfig {
  const v = c && typeof c === 'object' ? c : {};
  return {
    flow: v.flow === 'v2' ? 'v2' : 'v1',
    explorer_picks: v.explorer_picks !== false,
    entry_modal: v.entry_modal === true,
    picks_card_depth: v.picks_card_depth === 'as_today' ? 'as_today'
      : v.picks_card_depth === 'full_except_contact' ? 'full_except_contact' : DEFAULT_CONFIG.picks_card_depth,
    hidden_industries: arr(v.hidden_industries).map(str),
    industry_order: arr(v.industry_order).map(str),
    copy: v.copy && typeof v.copy === 'object' ? v.copy : {},
    picks: v.picks && typeof v.picks === 'object' ? v.picks : {},
    lock_search: boolOrNull(v.lock_search),
    _before_go_live: v._before_go_live && typeof v._before_go_live === 'object' ? v._before_go_live : null,
  };
}

const sourceOf = (raw: any): { source: string; by: string } => {
  const s = str(raw).trim();
  if (s.startsWith('admin:')) return { source: 'admin', by: s.slice(6) };
  if (s === 'admin' || s === 'corpus' || s === 'default' || s === 'none') return { source: s, by: '' };
  return { source: s || 'none', by: '' };
};

function normIndustry(r: any, hidden: string[]): IndustryRow | null {
  if (!r) return null;
  const name = str(typeof r === 'string' ? r : first(r.name, r.industry)).trim();
  if (!name) return null;
  const chipsRaw = typeof r === 'string' ? [] : first(r.chips, r.keywords, []);
  const src = sourceOf(typeof r === 'string' ? '' : first(r.source, r.chips_source));
  const countsRaw = typeof r === 'string' ? null : first(r.counts, r.chip_counts, r.strict_counts, r.matches);
  const counts: Record<string, number> = {};
  if (countsRaw && typeof countsRaw === 'object' && !Array.isArray(countsRaw)) {
    Object.entries(countsRaw).forEach(([k, v]) => { const n = numOrNull(v); if (n !== null) counts[k] = n; });
  } else if (Array.isArray(countsRaw)) {
    countsRaw.forEach((x: any) => {
      const k = str(first(x?.keyword, x?.chip, x?.name)); const n = numOrNull(first(x?.candidates, x?.count, x?.n));
      if (k && n !== null) counts[k] = n;
    });
  }
  return {
    name,
    chips: arr(chipsRaw).map(str).filter(Boolean),
    source: src.source,
    by: str(first(typeof r === 'string' ? '' : r.updated_by, src.by)),
    updated_at: typeof r === 'string' ? null : (first(r.updated_at, r.updated) ?? null),
    hidden: typeof r === 'string' ? hidden.includes(name) : (typeof r.hidden === 'boolean' ? r.hidden : hidden.includes(name)),
    counts,
    candidates: typeof r === 'string' ? null : numOrNull(first(r.candidates, r.industry_candidates)),
  };
}

export function normConfigData(d: any): ConfigData {
  const raw = d || {};
  const config = normConfig(first(raw.config, raw.settings, raw));
  // lock_search may come beside the config rather than inside it.
  if (config.lock_search === null) config.lock_search = boolOrNull(raw.lock_search);
  const defCopy = { ...DEFAULT_ONBOARDING_COPY };
  const dc = first(raw.defaults?.copy, raw.default_copy);
  if (dc && typeof dc === 'object') {
    Object.entries(dc).forEach(([k, v]) => { if (typeof v === 'string' && k in defCopy) (defCopy as any)[k] = v; });
  }
  const indRaw = arr(first(raw.industries, raw.keywords_by_industry, raw.rows));
  const industries = indRaw.map(r => normIndustry(r, config.hidden_industries)).filter(Boolean) as IndustryRow[];
  const allNames = arr(first(raw.all_industries, raw.industry_options, raw.options))
    .map((x: any) => str(typeof x === 'string' ? x : x?.name)).filter(Boolean);
  const names = allNames.length ? allNames : industries.map(i => i.name);
  const ck = first(raw.checklist, raw.go_live_checklist, raw.go_live, {}) || {};
  const lock = first(ck.free_tier_lock, ck.lock, raw.free_tier_lock, raw.lock, {}) || {};
  const pb = first(ck.picks_block, ck.upgrade_prompts?.picks_block, raw.picks_block, {}) || {};
  const sm = first(ck.signin_modal, ck.upgrade_prompts?.signin_modal, raw.signin_modal, {}) || {};
  const ps = first(ck.picks, ck.picks_status, raw.picks_status, {}) || {};
  const thin = first(ps.thin, ps.thin_lists);
  return {
    config,
    defaults: defCopy,
    config_version: str(first(raw.config_version, raw.public?.config_version, '')),
    industries,
    all_names: names,
    checklist: {
      lock: {
        enabled: boolOrNull(lock.enabled),
        can_search: boolOrNull(lock.can_search),
        preview_results: numOrNull(lock.preview_results),
        can_filter: boolOrNull(lock.can_filter),
      },
      picks_block_enabled: boolOrNull(typeof pb === 'boolean' ? pb : pb.enabled),
      signin_modal_enabled: boolOrNull(typeof sm === 'boolean' ? sm : sm.enabled),
      picks_built_at: first(ps.built_at, ps.last_build?.finished_at, ps.last_build?.started_at, ps.last_built_at) ?? null,
      thin_count: Array.isArray(thin) ? thin.length : numOrNull(first(thin, ps.thin_count)),
      ids_missing: numOrNull(first(ps.ids_missing, ps.missing)),
    },
    raw,
  };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
export const fmt = (n: any): string => {
  const v = numOrNull(n);
  return v === null ? '—' : v.toLocaleString();
};
export const pct = (num: any, den: any): string => {
  const a = numOrNull(num); const b = numOrNull(den);
  if (a === null || !b) return '—';
  return `${Math.round((a / b) * 1000) / 10}%`;
};
/** Seconds (or ms when `ms`) as "42 s" / "3 min 5 s". */
export const secs = (v: any, ms = false): string => {
  const n = numOrNull(v);
  if (n === null) return '—';
  const s = ms ? n / 1000 : n;
  if (s < 60) return `${Math.round(s * 10) / 10} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s - m * 60)} s`;
};
export const ago = (iso: any): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return String(iso);
  const d = Math.max(0, Date.now() - t) / 1000;
  if (d < 90) return 'just now';
  if (d < 3600) return `${Math.round(d / 60)} min ago`;
  if (d < 86400 * 2) return `${Math.round(d / 3600)} h ago`;
  return `${Math.round(d / 86400)} days ago`;
};
export const num = numOrNull;
export const asArray = arr;
export const asStr = str;

/** Pull a list of rows out of whatever key the server used. */
export const rowsOf = (d: any, ...keys: string[]): any[] => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== 'object') return [];
  for (const k of [...keys, 'rows', 'items', 'data', 'results']) {
    if (Array.isArray(d[k])) return d[k];
  }
  return [];
};

export const SOURCE_LABEL: Record<string, string> = {
  admin: 'Admin', corpus: 'Corpus', default: 'Built-in', none: 'None',
};

export const Pill: React.FC<{ tone?: 'ok' | 'warn' | 'bad' | 'grey' | 'tan'; children: React.ReactNode; title?: string }> =
  ({ tone = 'grey', children, title }) => {
  const look = {
    ok: 'bg-green-50 text-green-800 border-green-200',
    warn: 'bg-amber-50 text-amber-800 border-amber-200',
    bad: 'bg-red-50 text-red-700 border-red-200',
    grey: 'bg-grayscale-200 text-grayscale-1200 border-grayscale-200',
    tan: 'bg-primary-100 text-primary-700 border-primary-200',
  }[tone];
  return (
    <span title={title}
      className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${look}`}>
      {children}
    </span>
  );
};

/** One line of the go-live checklist: label, current value, and whether it is what Go live sets. */
export const CheckRow: React.FC<{ label: string; value: React.ReactNode; ok: boolean | null; hint?: string }> =
  ({ label, value, ok, hint }) => (
  <div className="flex items-start gap-2 text-sm py-1">
    <span className={`mt-0.5 w-4 text-center ${ok === null ? 'text-grayscale-600' : ok ? 'text-green-700' : 'text-amber-700'}`}
      aria-hidden="true">{ok === null ? '·' : ok ? '✓' : '!'}</span>
    <span className="flex-1 min-w-0">
      <span className="text-grayscale-1200">{label}</span>
      {hint && <span className="block text-[11px] text-grayscale-1100">{hint}</span>}
    </span>
    <span className="text-grayscale-1300 font-medium text-right">{value}</span>
  </div>
);

export const yesNo = (v: boolean | null, yes = 'On', no = 'Off') => (v === null ? '—' : v ? yes : no);

/** Today and N days back, as YYYY-MM-DD (IST calendar days, the funnel's unit). */
export const istDay = (offsetDays = 0): string => {
  const now = new Date(Date.now() + 5.5 * 3600 * 1000 - offsetDays * 86400 * 1000);
  return now.toISOString().slice(0, 10);
};
